(() => {
  'use strict';
  const host = typeof window === 'undefined' ? globalThis : window;
  const API_PATH = '/api/2026-10/graphql.json';
  const QUOTE_QUERY = `mutation RNFQuickBuyPreview($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        discountCodes { code applicable }
        cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
        lines(first: 100) {
          nodes {
            quantity attributes { key value }
            merchandise { ... on ProductVariant { id } }
            sellingPlanAllocation { sellingPlan { id } }
            cost { amountPerQuantity { amount currencyCode } totalAmount { amount currencyCode } }
            discountAllocations {
              __typename targetType discountedAmount { amount currencyCode }
              ... on CartAutomaticDiscountAllocation { title }
              ... on CartCodeDiscountAllocation { code }
              ... on CartCustomDiscountAllocation { title }
            }
            allDiscountAllocations: discountAllocations(lineLevelOnly: false) {
              __typename targetType discountedAmount { amount currencyCode }
              ... on CartAutomaticDiscountAllocation { title }
              ... on CartCodeDiscountAllocation { code }
              ... on CartCustomDiscountAllocation { title }
            }
          }
          pageInfo { hasNextPage }
        }
      }
      userErrors { code field message }
      warnings { code message }
    }
  }`;
  const VARIANT_QUERY = `query RNFQuoteVariant($id: ID!, $country: CountryCode!) @inContext(country: $country) {
    node(id: $id) { ... on ProductVariant { id price { amount currencyCode } product { id } } }
  }`;

  const transientReasons = new Set(['quote_network_failed', 'quote_http_transient', 'quote_api_transient', 'quote_timeout', 'cart_read_transient']);
  const nonBlockingDiscountWarnings = new Set(['DISCOUNT_CODE_NOT_HONOURED', 'DISCOUNT_CURRENTLY_INACTIVE', 'DISCOUNT_INCOMPATIBLE_PURCHASE_TYPE', 'DISCOUNT_NO_ENTITLED_LINE_ITEMS', 'DISCOUNT_NO_ENTITLED_SHIPPING_LINES', 'DISCOUNT_NOT_FOUND', 'DISCOUNT_PURCHASE_NOT_IN_RANGE', 'DISCOUNT_QUANTITY_NOT_IN_RANGE', 'DISCOUNT_USAGE_LIMIT_REACHED']);
  const fail = reason => ({ available: false, unitCents: null, totalCents: null, currency: null, discounts: [], codes: [], reason, retryable: transientReasons.has(reason) });
  const gid = (type, id) => `gid://shopify/${type}/${String(id).replace(/^.*\//, '')}`;
  const numericId = id => String(id || '').replace(/^.*\//, '');
  const propertyPairs = properties => Object.entries(properties || {}).filter(([, value]) => value != null).map(([key, value]) => ({ key, value: String(value) })).sort((a, b) => a.key.localeCompare(b.key));
  const pairsObject = pairs => Object.fromEntries((pairs || []).map(pair => [pair.key, pair.value]));
  const identity = (variant, attributes, sellingPlan) => JSON.stringify([numericId(variant), propertyPairs(pairsObject(attributes)), numericId(sellingPlan)]);
  const cartCurrency = cart => typeof cart.currency === 'string' ? cart.currency : cart.currency?.iso_code;

  function moneyCents(money, currency) {
    if (money?.currencyCode !== currency || !/^\d+(?:\.\d+)?$/.test(String(money?.amount))) throw new Error('currency_or_money_invalid');
    const scaled = Number(money.amount) * 100;
    const result = Math.round(scaled);
    if (!Number.isSafeInteger(result) || Math.abs(result - scaled) > 0.000001) throw new Error('money_precision_unsupported');
    return result;
  }

  function extractCodes(cart) {
    const codes = [];
    const add = code => { if (typeof code === 'string' && code.trim() && !codes.some(value => value.toUpperCase() === code.trim().toUpperCase())) codes.push(code.trim()); };
    if (Array.isArray(cart.discount_codes)) cart.discount_codes.forEach(entry => add(typeof entry === 'string' ? entry : entry?.code));
    const applications = [...(cart.cart_level_discount_applications || [])];
    for (const item of cart.items || []) applications.push(...(item.line_level_discount_allocations || []).map(allocation => allocation.discount_application));
    applications.forEach(application => { if (application?.type === 'discount_code') add(application.code || application.title); });
    return codes;
  }

  function snapshotFingerprint(cart) {
    const applications = (cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => [allocation.discount_application?.type, allocation.discount_application?.title]));
    return JSON.stringify([cartCurrency(cart), cart.total_price, (cart.items || []).map(item => [item.variant_id || item.id, item.quantity, propertyPairs(item.properties), item.selling_plan_allocation?.selling_plan?.id || null]), applications, (cart.cart_level_discount_applications || []).map(application => [application.type, application.title])]);
  }

  function projectCart(cart, variantId, quantity, productId, config, lineProperties = {}) {
    if (!Array.isArray(cart.items) || !Array.isArray(config?.gifts)) throw new Error('gift_rules_missing');
    if (cart.items.some(item => item.has_components || item.item_components?.length)) throw new Error('cart_components_unsupported');
    if (config.gifts.some(rule => numericId(rule.variant) === numericId(variantId)) || numericId(config.protection) === numericId(variantId)) throw new Error('non_merchandise');
    const entries = cart.items.map(item => {
      const quantity = Number(item.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('snapshot_quantity_invalid');
      const line = { merchandiseId: gid('ProductVariant', item.variant_id || item.id), quantity, attributes: propertyPairs(item.properties) };
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlan) line.sellingPlanId = gid('SellingPlan', sellingPlan);
      return { productId: numericId(item.product_id), line };
    });
    const targetAttributes = propertyPairs(lineProperties);
    const targetIdentity = identity(variantId, targetAttributes, null);
    const previous = entries.filter(entry => identity(entry.line.merchandiseId, entry.line.attributes, entry.line.sellingPlanId) === targetIdentity);
    if (previous.length > 1) throw new Error('ambiguous_snapshot_line');
    const existingQuantity = previous[0]?.line.quantity || 0;
    if (previous[0]) previous[0].line.quantity += quantity;
    else entries.push({ productId: numericId(productId), line: { merchandiseId: gid('ProductVariant', variantId), quantity, attributes: targetAttributes } });
    for (const rule of config.gifts) {
      if (!rule.variant || !Array.isArray(rule.products)) throw new Error('gift_rules_invalid');
      const giftQuantity = entries.filter(entry => rule.products.includes(entry.productId)).reduce((sum, entry) => sum + entry.line.quantity, 0);
      const eligible = giftQuantity > 0;
      const matches = entries.filter(entry => numericId(entry.line.merchandiseId) === numericId(rule.variant));
      matches.slice(eligible ? 1 : 0).forEach(entry => entries.splice(entries.indexOf(entry), 1));
      if (eligible && matches[0]) matches[0].line.quantity = giftQuantity;
      else if (eligible) {
        if (rule.available !== true) throw new Error('included_gift_unavailable');
        entries.push({ productId: '', line: { merchandiseId: gid('ProductVariant', rule.variant), quantity: giftQuantity, attributes: [{ key: '_rnf_auto_gift', value: String(rule.id) }] } });
      }
    }
    if (entries.length > 80) throw new Error('cart_too_large');
    return { lines: entries.map(entry => entry.line), targetIdentity, expectedQuantity: existingQuantity + quantity };
  }

  function resultFromCart(cart, projection, requestedQuantity, currency, baseCents) {
    if (!cart || cart.lines?.pageInfo?.hasNextPage || !Array.isArray(cart.lines?.nodes)) throw new Error('quote_incomplete');
    const lines = cart.lines.nodes;
    const target = lines.filter(line => identity(line.merchandise?.id, line.attributes, line.sellingPlanAllocation?.sellingPlan?.id) === projection.targetIdentity);
    if (target.length !== 1 || target[0].quantity !== projection.expectedQuantity) throw new Error('selected_line_changed');
    const line = target[0];
    if (moneyCents(line.cost?.amountPerQuantity, currency) !== baseCents) throw new Error('market_price_mismatch');
    const allLinesTotal = lines.reduce((sum, item) => sum + moneyCents(item.cost?.totalAmount, currency), 0);
    const subtotalCents = moneyCents(cart.cost?.subtotalAmount, currency);
    const cartTotalCents = moneyCents(cart.cost?.totalAmount, currency);
    if (subtotalCents !== allLinesTotal) throw new Error('unallocated_cart_discount');
    let completeLineCents = moneyCents(line.cost?.totalAmount, currency);
    let selectedAllocations = line.discountAllocations || [];
    const orderAllocations = (cart.discountAllocations || []).filter(allocation => allocation.targetType === 'LINE_ITEM');
    const orderDiscountCents = orderAllocations.reduce((sum, allocation) => sum + moneyCents(allocation.discountedAmount, currency), 0);
    if (lines.every(item => Array.isArray(item.allDiscountAllocations))) {
        const allocationTotal = allocations => allocations.filter(allocation => !allocation.targetType || allocation.targetType === 'LINE_ITEM').reduce((sum, allocation) => sum + moneyCents(allocation.discountedAmount, currency), 0);
        let allocatedOrderCents = 0;
        for (const item of lines) {
          const extraDiscountCents = allocationTotal(item.allDiscountAllocations) - allocationTotal(item.discountAllocations || []);
          const itemTotalCents = moneyCents(item.cost?.totalAmount, currency);
          if (extraDiscountCents < 0 || extraDiscountCents > itemTotalCents) throw new Error('unallocated_cart_discount');
          allocatedOrderCents += extraDiscountCents;
          if (item === line) completeLineCents = itemTotalCents - extraDiscountCents;
        }
        if ((allocatedOrderCents > 0 || cartTotalCents < subtotalCents) && allocatedOrderCents !== subtotalCents - cartTotalCents) throw new Error('unallocated_cart_discount');
        if (Array.isArray(cart.discountAllocations) && allocatedOrderCents !== orderDiscountCents) throw new Error('unallocated_cart_discount');
        selectedAllocations = line.allDiscountAllocations;
    } else if (cartTotalCents < subtotalCents || orderDiscountCents > 0) {
        if (orderDiscountCents !== subtotalCents - cartTotalCents) throw new Error('unallocated_cart_discount');
        const paidLines = lines.filter(item => moneyCents(item.cost?.totalAmount, currency) > 0);
        if (paidLines.length !== 1 || paidLines[0] !== line) throw new Error('unallocated_cart_discount');
        completeLineCents = cartTotalCents;
        selectedAllocations = [...selectedAllocations, ...orderAllocations];
    }
    const unitCents = completeLineCents / line.quantity;
    const totalCents = unitCents * requestedQuantity;
    if (![unitCents, totalCents].every(value => Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER)) throw new Error('quote_amount_invalid');
    const estimated = Math.abs(totalCents - Math.round(totalCents)) > 0.000001;
    const discountMap = new Map();
    for (const allocation of selectedAllocations.filter(allocation => !allocation.targetType || allocation.targetType === 'LINE_ITEM')) {
      const amountCents = moneyCents(allocation.discountedAmount, currency) * requestedQuantity / line.quantity;
      const title = allocation.title || allocation.code;
      if (!title && amountCents) throw new Error('discount_title_missing');
      if (amountCents) discountMap.set(title, (discountMap.get(title) || 0) + amountCents);
    }
    return { available: true, unitCents, totalCents: Math.round(totalCents), currency, discounts: [...discountMap].map(([title, amountCents]) => ({ title, amountCents })), codes: (cart.discountCodes || []).filter(code => code.applicable === true).map(code => code.code), estimated, reason: estimated ? 'merged_line_rounding_estimate' : null };
  }

  function readJson(selector) {
    const node = host.document?.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (_) { return null; }
  }

  function retryDelay(signal) {
    return new Promise((resolve, reject) => {
      const onAbort = () => { clearTimeout(timer); reject(new Error('session_disposed')); };
      const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, 250);
      if (signal?.aborted) onAbort();
      else signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  async function retryTransient(task, signal) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try { return await task(); }
      catch (error) {
        if (signal?.aborted || !transientReasons.has(error.message) || attempt === 1) throw error;
        await retryDelay(signal);
      }
    }
  }

  async function storefront(query, variables, signal) {
    let response;
    try { response = await host.fetch(new URL(API_PATH, host.location.origin).href, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal, body: JSON.stringify({ query, variables }) }); }
    catch (error) { if (signal?.aborted) throw error; throw new Error('quote_network_failed'); }
    if (!response.ok) throw new Error(response.status === 408 || response.status === 429 || response.status >= 500 ? 'quote_http_transient' : 'quote_http_failed');
    const payload = await response.json();
    if (payload.errors?.length || !payload.data) {
      const transient = payload.errors?.length && payload.errors.every(error => ['THROTTLED', 'INTERNAL_SERVER_ERROR'].includes(error.extensions?.code));
      throw new Error(transient ? 'quote_api_transient' : 'quote_api_failed');
    }
    return payload.data;
  }

  const validRequest = (variantId, quantity) => /^[1-9]\d*$/.test(numericId(variantId)) && Number.isSafeInteger(quantity) && quantity > 0;
  const copyQuote = quote => ({ ...quote, discounts: quote.discounts.map(discount => ({ ...discount })), codes: [...quote.codes] });
  const quoteKey = (variantId, quantity) => JSON.stringify([numericId(variantId), quantity]);

  function unavailableSession(reason) {
    return { available: false, reason, retryable: transientReasons.has(reason), fingerprint: null, getQuote: async () => fail(reason), peekQuote: () => null, dispose() {} };
  }

  function waitForCaller(promise, signal) {
    if (!signal) return promise.then(copyQuote);
    if (signal.aborted) return Promise.resolve(fail('aborted'));
    return new Promise(resolve => {
      let settled = false;
      const finish = result => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', onAbort);
        resolve(copyQuote(result));
      };
      const onAbort = () => finish(fail('aborted'));
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(finish, error => finish(fail(error.message || 'quote_failed')));
    });
  }

  async function createSession({ country, currency, loggedIn, signal, lineProperties = {}, productData } = {}) {
    if (loggedIn) return unavailableSession('customer_identity_unavailable');
    if (!/^[A-Z]{2}$/.test(country || '') || !/^[A-Z]{3}$/.test(currency || '')) return unavailableSession('request_invalid');
    if (signal?.aborted) return unavailableSession('aborted');
    const lifetime = new AbortController();
    let disposed = false;
    const metadata = new Map(), pending = new Map(), quotes = new Map();
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      lifetime.abort();
      signal?.removeEventListener('abort', dispose);
      quotes.clear();
      pending.clear();
      metadata.clear();
    };
    signal?.addEventListener('abort', dispose, { once: true });
    const timer = setTimeout(() => lifetime.abort(), 12000);
    let snapshot, config, codes;
    try {
      const root = new URL(host.Shopify?.routes?.root || '/', host.location.origin);
      if (root.origin !== host.location.origin) throw new Error('cart_route_invalid');
      const cartUrl = new URL('cart.js', root.href.endsWith('/') ? root : `${root.href}/`);
      const response = await retryTransient(async () => {
        let result;
        try { result = await host.fetch(cartUrl.href, { credentials: 'same-origin', cache: 'no-store', signal: lifetime.signal }); }
        catch (error) { if (lifetime.signal.aborted) throw error; throw new Error('cart_read_transient'); }
        if (!result.ok) throw new Error(result.status === 408 || result.status === 429 || result.status >= 500 ? 'cart_read_transient' : 'cart_read_failed');
        return result;
      }, lifetime.signal);
      snapshot = await response.json();
      if (cartCurrency(snapshot) !== currency) throw new Error('cart_market_mismatch');
      config = readJson('[data-rnf-cart-config]');
      if (!Array.isArray(snapshot.items) || !Array.isArray(config?.gifts)) throw new Error('gift_rules_missing');
      codes = extractCodes(snapshot);
      const renderedState = readJson('[data-rnf-cart-state]');
      if (!Array.isArray(snapshot.discount_codes) && renderedState && snapshotFingerprint(snapshot) === snapshotFingerprint(renderedState)) codes = [...new Set([...codes, ...extractCodes(renderedState)])];
      const wrapper = host.document?.querySelector('[data-rnf-qb-product]');
      const context = productData || { id: wrapper?.dataset.productId, country: wrapper?.dataset.country, currency: wrapper?.dataset.currency, variants: readJson('[data-rnf-qb-variants]') };
      const variants = context.variants;
      if (context.id && context.country === country && context.currency === currency && Array.isArray(variants)) {
        for (const variant of variants) {
          const baseCents = Number(variant.baseCents);
          if (/^[1-9]\d*$/.test(numericId(variant.id)) && Number.isSafeInteger(baseCents) && baseCents >= 0) metadata.set(numericId(variant.id), Promise.resolve({ productId: numericId(context.id), baseCents }));
        }
      }
    } catch (error) {
      const reason = lifetime.signal.aborted ? signal?.aborted ? 'aborted' : 'quote_timeout' : error.message || 'quote_failed';
      dispose();
      return unavailableSession(reason);
    } finally { clearTimeout(timer); }

    const fingerprint = JSON.stringify([country, currency, snapshotFingerprint(snapshot), propertyPairs(snapshot.attributes), snapshot.note || '', codes, config.gifts]);

    async function timedTask(task) {
      const controller = new AbortController();
      const onAbort = () => controller.abort();
      lifetime.signal.addEventListener('abort', onAbort, { once: true });
      if (lifetime.signal.aborted) controller.abort();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try { return await task(controller.signal); }
      catch (error) {
        if (controller.signal.aborted) throw new Error(lifetime.signal.aborted ? 'session_disposed' : 'quote_timeout');
        throw error;
      } finally {
        clearTimeout(timeout);
        lifetime.signal.removeEventListener('abort', onAbort);
      }
    }

    function variantMetadata(variantId) {
      const key = numericId(variantId);
      if (metadata.has(key)) return metadata.get(key);
      const request = retryTransient(() => timedTask(async requestSignal => {
        const data = await storefront(VARIANT_QUERY, { id: gid('ProductVariant', variantId), country }, requestSignal);
        if (!data.node?.product?.id) throw new Error('variant_metadata_missing');
        return { productId: numericId(data.node.product.id), baseCents: moneyCents(data.node.price, currency) };
      }), lifetime.signal);
      metadata.set(key, request);
      request.catch(() => { if (metadata.get(key) === request) metadata.delete(key); });
      return request;
    }

    async function requestQuote(variantId, quantity) {
      try {
        const known = await variantMetadata(variantId);
        if (disposed) return fail('session_disposed');
        const projection = projectCart(snapshot, variantId, quantity, known.productId, config, lineProperties);
        const input = { buyerIdentity: { countryCode: country }, lines: projection.lines, discountCodes: codes, attributes: propertyPairs(snapshot.attributes) };
        if (typeof snapshot.note === 'string') input.note = snapshot.note;
        const data = await retryTransient(() => timedTask(requestSignal => storefront(QUOTE_QUERY, { input }, requestSignal)), lifetime.signal);
        if (disposed) return fail('session_disposed');
        const payload = data.cartCreate;
        if (payload?.warnings?.some(warning => ['DISCOUNT_ELIGIBLE_CUSTOMER_MISSING', 'DISCOUNT_CUSTOMER_NOT_ELIGIBLE', 'DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED'].includes(warning.code))) throw new Error('customer_identity_unavailable');
        if (payload?.userErrors?.length || payload?.warnings?.some(warning => !nonBlockingDiscountWarnings.has(warning.code))) throw new Error('quote_rejected');
        return resultFromCart(payload?.cart, projection, quantity, currency, known.baseCents);
      } catch (error) {
        return fail(disposed ? 'session_disposed' : error.message || 'quote_failed');
      }
    }

    function getSessionQuote({ variantId, quantity, signal: callerSignal } = {}) {
      if (!validRequest(variantId, quantity)) return Promise.resolve(fail('request_invalid'));
      if (disposed) return Promise.resolve(fail('session_disposed'));
      if (callerSignal?.aborted) return Promise.resolve(fail('aborted'));
      const key = quoteKey(variantId, quantity);
      if (quotes.has(key)) return Promise.resolve(copyQuote(quotes.get(key)));
      if (!pending.has(key)) {
        const request = requestQuote(variantId, quantity).then(result => {
          if (result.available && !disposed) quotes.set(key, copyQuote(result));
          return result;
        }).finally(() => { if (pending.get(key) === request) pending.delete(key); });
        pending.set(key, request);
      }
      return waitForCaller(pending.get(key), callerSignal);
    }

    return { available: true, reason: null, fingerprint, getQuote: getSessionQuote, peekQuote({ variantId, quantity } = {}) { const cached = !disposed && quotes.get(quoteKey(variantId, quantity)); return cached ? copyQuote(cached) : null; }, dispose };
  }

  async function getQuote({ variantId, quantity, country, currency, loggedIn, signal, lineProperties = {} } = {}) {
    if (!validRequest(variantId, quantity)) return fail('request_invalid');
    const session = await createSession({ country, currency, loggedIn, signal, lineProperties });
    try { return await session.getQuote({ variantId, quantity, signal }); }
    finally { session.dispose(); }
  }

  host.RNFQuickBuyQuote = { createSession, getQuote };
  if (typeof module !== 'undefined' && module.exports) module.exports = { extractCodes, projectCart, resultFromCart, moneyCents, snapshotFingerprint, QUOTE_QUERY, VARIANT_QUERY, createSession, getQuote };
})();

(() => {
  function resolveVariant(variants, values) {
    return variants.find(variant => variant.options.length === values.length && variant.options.every((value, index) => value === values[index])) || null;
  }

  function normalizeQuantity(value, variant) {
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    const max = variant.max == null ? Infinity : Math.max(min, Number(variant.max));
    const requested = Number(value);
    const aligned = min + Math.max(0, Math.floor(((Number.isFinite(requested) ? requested : min) - min) / step)) * step;
    return Math.min(aligned, min + Math.floor((max - min) / step) * step);
  }

  function formatPrice(cents, currency, locale, country) {
    const language = (locale || 'en').replace('_', '-');
    const regional = language.includes('-') || !country ? language : `${language}-${country}`;
    const money = new Intl.NumberFormat(regional, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(Number(cents) / 100);
    return money.includes(currency) ? money : `${money} ${currency}`;
  }

  function shouldDismissDrag(distance, elapsed) {
    return distance >= 88 || (distance >= 35 && distance / Math.max(1, elapsed) > 0.5);
  }

  function createQuantityPricing(fetchQuote, variants, onUpdate, signal) {
    const groupKey = variant => JSON.stringify([variant.baseCents, variant.min || 1, variant.step || 1, variant.max ?? null]);
    const key = (variant, quantity) => `${groupKey(variant)}:${quantity}`;
    const representatives = new Map();
    variants.filter(variant => variant.available).forEach(variant => { if (!representatives.has(groupKey(variant))) representatives.set(groupKey(variant), variant); });
    const results = new Map(), jobs = new Map(), queue = [];
    let active = 0;
    function pump() {
      while (active < 3 && queue.length && !signal.aborted) {
        const job = queue.shift();
        active++;
        Promise.resolve().then(() => fetchQuote({ variantId: job.variant.id, quantity: job.quantity, signal })).catch(() => ({ available: false })).then(result => {
          if (signal.aborted) return;
          results.set(job.key, { ...result, quantity: job.quantity, group: groupKey(job.variant) });
          onUpdate(job.variant, job.quantity);
        }).finally(() => { if (jobs.get(job.key) === job) jobs.delete(job.key); active--; pump(); });
      }
    }
    function load(variant, quantity, priority = false) {
      if (!variant?.available || signal.aborted) return;
      const quoteKey = key(variant, quantity);
      const cached = results.get(quoteKey);
      if (cached && (cached.available || cached.retryable !== true)) return;
      if (jobs.has(quoteKey)) {
        const pending = queue.findIndex(job => job.key === quoteKey);
        if (priority && pending > 0) queue.unshift(...queue.splice(pending, 1));
        pump();
        return;
      }
      const representative = representatives.get(groupKey(variant)) || variant;
      const job = { key: quoteKey, variant: representative, quantity };
      jobs.set(quoteKey, job);
      if (priority) queue.unshift(job); else queue.push(job);
      pump();
    }
    function prime(variant, quantity) {
      load(variant, quantity, true);
    }
    return { read: (variant, quantity) => variant ? results.get(key(variant, quantity)) : null, load, prime, groupKey };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { resolveVariant, normalizeQuantity, formatPrice, shouldDismissDrag, createQuantityPricing };
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('RnfQuickBuy');
  if (!dialog || dialog.dataset.initialized) return;
  dialog.dataset.initialized = 'true';
  const mount = dialog.querySelector('[data-rnf-qb-mount]');
  const state = dialog.querySelector('.rnf-qb__state');
  const status = dialog.querySelector('[data-rnf-qb-status]');
  const retry = dialog.querySelector('[data-rnf-qb-retry]');
  const closeButton = dialog.querySelector('.rnf-qb__handle') || dialog.querySelector('[data-rnf-qb-close]');
  const dragHeader = dialog.querySelector('.rnf-qb__header');
  const secure = dialog.querySelector('[data-rnf-qb-secure]');
  const secureIntro = secure.querySelector('[data-rnf-qb-secure-intro]');
  const secureStatus = secure.querySelector('[data-rnf-qb-secure-status]');
  const secureCart = secure.querySelector('[data-rnf-qb-secure-cart]');
  const secureBack = secure.querySelector('[data-rnf-qb-secure-back]');
  const secureViewCart = secure.querySelector('[data-rnf-qb-secure-view-cart]');
  const cache = new Map();
  let pendingProducts = 0;
  let opener, productUrl, request, requestVersion = 0, product, variants = [], currentVariant, busy = false;
  let lock = null, openFrame = null, closePromise = null, closeTimer = null;
  let keyboardOpener = false, quoteVersion = 0, quoteController = null, lastQuote = null, pricing = null, quoteSession = null;
  let drag = null, suppressCloseClickUntil = 0, swapping = false, mediaVersion = 0;
  let paymentAttempted = false, paymentController = null, offerContext = null;
  let quoteRecoveryTimer = null;
  let initialPriceReady = Promise.resolve(), preparedPricing = null;
  let disposeProductUI = () => {};
  const quoteRecoveries = new Set();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let googlePaySDK;

  function loadGooglePayReadiness() {
    if (window.google?.payments?.api?.PaymentsClient) return Promise.resolve();
    if (googlePaySDK) return googlePaySDK;
    googlePaySDK = new Promise((resolve, reject) => {
      const source = 'https://pay.google.com/gp/p/js/pay.js';
      let script = document.querySelector(`script[src="${source}"]`);
      const created = !script;
      if (!script) { script = document.createElement('script'); script.src = source; script.async = true; }
      let timer;
      const finish = error => {
        clearTimeout(timer);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
        if (error) { googlePaySDK = null; if (created) script.remove(); reject(error); }
        else resolve();
      };
      const loaded = () => finish(window.google?.payments?.api?.PaymentsClient ? null : new Error('Google Pay unavailable'));
      const failed = () => finish(new Error('Google Pay unavailable'));
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      timer = setTimeout(failed, 5000);
      if (created) document.head.append(script);
    });
    return googlePaySDK;
  }

  const detectedDeviceWallets = new Set();
  let walletReadiness;
  function primeWallet(root) {
    if (walletReadiness) return walletReadiness;
    const probe = root.querySelector('[data-rnf-qb-wallet-probe]');
    if (!probe) return Promise.resolve();
    if (probe.dataset.applePay === 'true') {
      try {
        if (window.ApplePaySession?.supportsVersion?.(6) && window.ApplePaySession.canMakePayments()) detectedDeviceWallets.add('apple_pay');
      } catch (_) {}
    }
    walletReadiness = (async () => {
      if (detectedDeviceWallets.has('apple_pay') || probe.dataset.googlePay !== 'true') return;
      try {
        await loadGooglePayReadiness();
        const client = new window.google.payments.api.PaymentsClient({ environment: 'PRODUCTION' });
        const result = await client.isReadyToPay({ apiVersion: 2, apiVersionMinor: 0, existingPaymentMethodRequired: true, allowedPaymentMethods: [{ type: 'CARD', parameters: { allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'], allowedCardNetworks: ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA'] } }] });
        if (result.result === true && result.paymentMethodPresent === true) detectedDeviceWallets.add('google_pay');
      } catch (_) {}
    })();
    return walletReadiness;
  }


  function mountProductUI(root) {
    const desktop = window.matchMedia('(min-width: 990px)');
    const quantity = root.querySelector('.rnf-qb__purchase-row');
    const slot = root.querySelector('[data-rnf-qb-quantity-slot]');
    const marker = document.createComment('rnf-quick-buy-quantity');
    if (quantity && slot) quantity.before(marker);
    const layout = () => {
      if (!quantity || !slot || !marker.parentNode) return;
      const destination = desktop.matches ? slot : marker.parentNode;
      if (quantity.parentNode === destination) return;
      if (desktop.matches) slot.append(quantity);
      else marker.after(quantity);
    };
    layout();
    if (desktop.addEventListener) desktop.addEventListener('change', layout);
    else desktop.addListener?.(layout);

    const probe = root.querySelector('[data-rnf-qb-wallet-probe]');
    const summary = root.querySelector('[data-rnf-qb-wallet-summary]');
    const walletTags = { apple_pay: 'shopify-apple-pay-button', google_pay: 'shopify-google-pay-button', paypal: 'shopify-paypal-button', shop_pay: 'shop-pay-wallet-button' };
    const deviceWallets = detectedDeviceWallets;
    const updateWallets = () => {
      if (!summary || !probe) return;
      const available = Object.entries(walletTags).filter(([name, selector]) => {
        const button = probe.querySelector(selector);
        return deviceWallets.has(name) || Boolean(button && !button.hidden && !button.hasAttribute('disabled'));
      });
      const selected = ['apple_pay', 'google_pay', 'shop_pay', 'paypal'].find(name => available.some(([candidate]) => candidate === name));
      summary.querySelectorAll('[data-rnf-qb-wallet]').forEach(logo => { logo.hidden = logo.dataset.rnfQbWallet !== selected; logo.querySelector('svg')?.setAttribute('aria-hidden', 'true'); });
      summary.hidden = !selected;
      const trigger = summary.closest('button');
      const caption = trigger?.querySelector(':scope > span')?.textContent.trim();
      const method = selected ? summary.querySelector('[data-rnf-qb-wallet="' + selected + '"] span')?.textContent.trim() : '';
      if (trigger && caption) trigger.setAttribute('aria-label', caption + (method ? ' · ' + method : ''));
    };
    let observer;
    const probeRequest = new AbortController();
    if (probe && summary && window.MutationObserver) {
      observer = new MutationObserver(updateWallets);
      observer.observe(probe, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
      updateWallets();
      primeWallet(root).then(() => { if (!probeRequest.signal.aborted && root.isConnected) updateWallets(); });
      (async () => {
        try {
          const url = new URL(probe.dataset.probeUrl, location.origin);
          if (url.origin !== location.origin) return;
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: probeRequest.signal });
          if (!response.ok) return;
          const html = await response.text();
          if (probeRequest.signal.aborted || !root.isConnected) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const template = parsed.querySelector('template[data-rnf-qb-wallet-template]');
          if (!template) return;
          template.content.querySelectorAll('script').forEach(script => script.remove());
          probe.replaceChildren(document.importNode(template.content, true));
          try { window.Shopify?.PaymentButton?.init?.(); } catch (_) {}
        } catch (_) { /* Keep the translated generic line when eligibility cannot be confirmed. */ }
      })();
    }
    const stopWalletProbe = () => { probeRequest.abort(); observer?.disconnect(); probe?.remove(); };
    root.addEventListener('rnf:quick-buy:payment', stopWalletProbe, { once: true });
    return () => {
      stopWalletProbe();
      root.removeEventListener('rnf:quick-buy:payment', stopWalletProbe);
      if (desktop.removeEventListener) desktop.removeEventListener('change', layout);
      else desktop.removeListener?.(layout);
      if (marker.parentNode) { marker.after(quantity); marker.remove(); }
    };
  }

  const saveStyle = (element, names) => names.map(name => [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
  const restoreStyle = (element, entries) => entries.forEach(([name, value, priority]) => value ? element.style.setProperty(name, value, priority) : element.style.removeProperty(name));

  function lockPage() {
    if (lock) return;
    const html = document.documentElement, body = document.body;
    lock = { x: window.scrollX, y: window.scrollY, body: saveStyle(body, ['position', 'top', 'left', 'width', 'overflow']), html: saveStyle(html, ['overflow', 'overscroll-behavior']) };
    body.style.setProperty('position', 'fixed', 'important');
    body.style.setProperty('top', `-${lock.y}px`, 'important');
    body.style.setProperty('left', `-${lock.x}px`, 'important');
    body.style.setProperty('width', '100%', 'important');
    body.style.setProperty('overflow', 'hidden', 'important');
    html.style.setProperty('overflow', 'hidden', 'important');
    html.style.setProperty('overscroll-behavior', 'none', 'important');
  }

  function unlockPage() {
    if (!lock) return;
    const saved = lock;
    lock = null;
    restoreStyle(document.body, saved.body);
    restoreStyle(document.documentElement, saved.html);
    const behavior = saveStyle(document.documentElement, ['scroll-behavior']);
    document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(saved.x, saved.y);
    restoreStyle(document.documentElement, behavior);
  }

  function viewport() {
    const view = window.visualViewport;
    dialog.style.setProperty('--rnf-qb-height', `${Math.round(view?.height || window.innerHeight)}px`);
    dialog.style.setProperty('--rnf-qb-bottom', `${Math.max(0, Math.round(window.innerHeight - (view?.height || window.innerHeight) - (view?.offsetTop || 0)))}px`);
  }

  function finishClose(restoreFocus) {
    disposeProductUI();
    disposeProductUI = () => {};
    clearTimeout(closeTimer);
    window.cancelAnimationFrame(openFrame);
    dialog.removeAttribute('data-visible');
    dialog.removeAttribute('data-variant-loading');
    dialog.removeAttribute('aria-busy');
    swapping = false;
    mediaVersion++;
    resetDrag();
    paymentController?.abort();
    if (dialog.open) dialog.close();
    mount.querySelectorAll('video').forEach(video => video.pause());
    unlockPage();
    if (restoreFocus && opener?.isConnected) {
      if (keyboardOpener) opener.focus({ preventScroll: true });
      else opener.blur();
    }
    dialog.removeAttribute('data-fast-close');
    if (!offerContext) warmVisibleProducts();
    if (restoreFocus && offerContext) {
      window.RNFStrapOffer?.reject?.();
      document.getElementById('RnfCartDrawer')?.reveal?.(opener);
    }
  }

  function close(restoreFocus = true, fast = Boolean(offerContext)) {
    if (closePromise) return closePromise;
    dialog.toggleAttribute('data-fast-close', fast);
    requestVersion++;
    request?.abort();
    cancelQuote();
    window.cancelAnimationFrame(openFrame);
    dialog.removeAttribute('data-visible');
    if (!dialog.open || reducedMotion()) { finishClose(restoreFocus); return Promise.resolve(); }
    closePromise = new Promise(resolve => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.removeEventListener('transitionend', onEnd);
        finishClose(restoreFocus);
        closePromise = null;
        resolve();
      };
      const onEnd = event => { if (event.target === dialog && event.propertyName === 'transform') finish(); };
      dialog.addEventListener('transitionend', onEnd);
      closeTimer = setTimeout(finish, fast ? 280 : 620);
    });
    return closePromise;
  }

  function loadState(error = false) {
    state.hidden = false;
    mount.hidden = true;
    dialog.toggleAttribute('data-load-error', error);
    status.textContent = error ? dialog.dataset.errorLabel : dialog.dataset.loadingLabel;
    status.classList.toggle('rnf-qb__sr', !error);
    retry.hidden = !error;
  }

  function revealProduct() {
    if (!dialog.open || dialog.hasAttribute('data-visible')) return;
    // Commit the final content and glass layer before starting the single entrance.
    window.getComputedStyle(dialog).transform;
    openFrame = window.requestAnimationFrame(() => {
      openFrame = window.requestAnimationFrame(() => { if (dialog.open) dialog.setAttribute('data-visible', ''); });
    });
  }

  async function readyImage(root, signal) {
    const image = root.querySelector('.rnf-qb__image');
    if (!image || typeof image.decode !== 'function') return;
    image.loading = 'eager';
    let timer, cancel;
    const cancelled = new Promise(resolve => {
      cancel = resolve;
      if (signal?.aborted) resolve();
      else if (signal) signal.addEventListener('abort', cancel, { once: true });
      else timer = setTimeout(resolve, 220);
    });
    try { await Promise.race([image.decode().catch(() => {}), cancelled]); }
    finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
  }

  function productKey(value) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || !/\/products\//.test(url.pathname)) throw new Error('Invalid product URL');
    url.searchParams.set('section_id', 'rnf-quick-buy');
    return url.href;
  }

  function productHtml(value, warm = false) {
    let key;
    try { key = productKey(value); } catch (error) { return Promise.reject(error); }
    const saved = cache.get(key);
    if (saved && Date.now() - saved.time < 60000) return saved.promise || Promise.resolve(saved.html);
    if (warm && pendingProducts >= 2) return Promise.resolve(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    const entry = { time: Date.now() };
    pendingProducts++;
    entry.promise = fetch(key, { credentials: 'same-origin', signal: controller.signal, cache: 'no-store' }).then(async response => {
      if (!response.ok) throw new Error(`Product HTTP ${response.status}`);
      const html = await response.text();
      if (cache.get(key) === entry) cache.set(key, { html, time: Date.now() });
      return html;
    }).catch(error => { if (cache.get(key) === entry) cache.delete(key); throw error; }).finally(() => { pendingProducts--; clearTimeout(timer); });
    cache.set(key, entry);
    if (cache.size > 12) {
      const oldest = [...cache].find(([, value]) => !value.promise);
      if (oldest) cache.delete(oldest[0]);
    }
    return entry.promise;
  }

  const preparedProducts = new Map();
  const warmQueue = new Map();
  let preparationGeneration = 0, activePreparation = 0;

  function forgetPreparation(key, entry) {
    if (preparedProducts.get(key) === entry) preparedProducts.delete(key);
    entry.controller.abort();
    entry.session?.dispose();
  }

  function invalidatePreparedProducts() {
    preparationGeneration++;
    for (const [key, entry] of preparedProducts) forgetPreparation(key, entry);
  }

  function prepareProduct(value) {
    const key = productKey(value);
    const saved = preparedProducts.get(key);
    if (saved && saved.generation === preparationGeneration && (!saved.ready || Date.now() - saved.time < 60000)) return saved.promise;
    if (saved) forgetPreparation(key, saved);
    const entry = { controller: new AbortController(), generation: preparationGeneration, time: Date.now(), ready: false };
    const signal = entry.controller.signal;
    preparedProducts.set(key, entry);
    while (preparedProducts.size > 12) {
      const oldest = [...preparedProducts].find(([otherKey, other]) => otherKey !== key && other.ready);
      if (!oldest) break;
      forgetPreparation(...oldest);
    }
    entry.promise = (async () => {
      const html = await productHtml(value);
      if (signal.aborted) throw new Error('Preparation cancelled');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const root = parsed.querySelector('[data-rnf-qb-product]');
      let preparedVariants;
      try { preparedVariants = JSON.parse(root?.querySelector('[data-rnf-qb-variants]')?.textContent || 'null'); }
      catch (_) { cache.delete(key); throw new Error('Missing product'); }
      if (!root || !Array.isArray(preparedVariants) || !preparedVariants.length) { cache.delete(key); throw new Error('Missing product'); }
      primeWallet(root);
      const selected = preparedVariants.find(variant => String(variant.id) === root.querySelector('[name="id"]').value) || preparedVariants[0];
      const quantity = normalizeQuantity(root.querySelector('[name="quantity"]').value, selected);
      const timeout = setTimeout(() => entry.controller.abort(), 15000);
      try {
        await Promise.all([
          readyImage(root, signal),
          (async () => {
            const session = await window.RNFQuickBuyQuote.createSession({ country: root.dataset.country, currency: root.dataset.currency, loggedIn: root.dataset.customer === 'true', signal, productData: { id: root.dataset.productId, country: root.dataset.country, currency: root.dataset.currency, variants: preparedVariants } });
            if (signal.aborted) { session.dispose(); return; }
            entry.session = session;
            if (selected.available) await session.getQuote({ variantId: selected.id, quantity, signal });
          })()
        ]);
      } finally { clearTimeout(timeout); }
      if (signal.aborted || entry.generation !== preparationGeneration) throw new Error('Preparation cancelled');
      entry.html = html;
      entry.root = root;
      entry.variants = preparedVariants;
      entry.ready = true;
      entry.time = Date.now();
      return entry;
    })().catch(error => { forgetPreparation(key, entry); throw error; });
    return entry.promise;
  }

  function takePreparation(value, entry) {
    if (preparedProducts.get(productKey(value)) === entry) preparedProducts.delete(productKey(value));
    return entry.session;
  }

  async function prepareForOpen(value, signal) {
    for (let attempt = 0; attempt < 3; attempt++) {
      const generation = preparationGeneration;
      try { return await prepareProduct(value); }
      catch (error) {
        if (signal.aborted || generation === preparationGeneration || attempt === 2) throw error;
      }
    }
  }

  function visibleTrigger(button) {
    if (!button?.isConnected || document.hidden) return false;
    const rect = button.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  }

  function drainWarmQueue() {
    if (dialog.open || busy || document.hidden) return;
    for (const [key, button] of warmQueue) {
      if (activePreparation >= 2) break;
      warmQueue.delete(key);
      if (!visibleTrigger(button)) continue;
      activePreparation++;
      prepareProduct(button.dataset.rnfQuickBuy).catch(() => {}).finally(() => { activePreparation--; drainWarmQueue(); });
    }
  }

  function queuePreparation(button) {
    if (!button?.dataset.rnfQuickBuy || !visibleTrigger(button)) return;
    let key;
    try { key = productKey(button.dataset.rnfQuickBuy); } catch (_) { return; }
    warmQueue.set(key, button);
    drainWarmQueue();
  }

  let visibleProducts;
  function observeProducts(root = document) {
    if (!visibleProducts) return;
    if (root.matches?.('[data-rnf-quick-buy]')) visibleProducts.observe(root);
    root.querySelectorAll?.('[data-rnf-quick-buy]').forEach(button => visibleProducts.observe(button));
  }

  function warmVisibleProducts() {
    document.querySelectorAll('[data-rnf-quick-buy]').forEach(queuePreparation);
  }

  function startVisiblePreparation() {
    if (!window.IntersectionObserver) return;
    visibleProducts = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) queuePreparation(entry.target); });
    });
    observeProducts();
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) observeProducts(node); }));
    }).observe(document.body, { childList: true, subtree: true });
  }

  const beginPreparation = () => {
    if (window.requestIdleCallback) window.requestIdleCallback(startVisiblePreparation, { timeout: 1500 });
    else setTimeout(startVisiblePreparation, 250);
  };
  if (document.readyState === 'complete') beginPreparation();
  else window.addEventListener('load', beginPreparation, { once: true });
  window.addEventListener('focus', () => { invalidatePreparedProducts(); warmVisibleProducts(); });


  async function loadProduct(preserve = false) {
    request?.abort();
    request = new AbortController();
    const controller = request;
    const version = ++requestVersion;
    controller.signal.addEventListener('abort', () => { if (version === requestVersion) cancelQuote(); }, { once: true });
    const key = productKey(productUrl);
    const previousProduct = preserve && product?.isConnected ? product : null;
    const scrollTop = previousProduct?.querySelector('.rnf-qb__scroll')?.scrollTop || 0;
    const restoreChoiceFocus = previousProduct?.contains(document.activeElement) && document.activeElement.matches('[data-rnf-strap-choice]');
    swapping = Boolean(previousProduct);
    dialog.toggleAttribute('data-variant-loading', swapping);
    dialog.setAttribute('aria-busy', 'true');
    if (previousProduct) setBusy(busy);
    else loadState();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const prepared = !preserve && !offerContext ? await prepareForOpen(productUrl, controller.signal) : null;
      const html = prepared ? prepared.html : await productHtml(productUrl);
      if (version !== requestVersion || !dialog.open) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-rnf-qb-product]');
      const data = next?.querySelector('[data-rnf-qb-variants]');
      let nextVariants;
      try { nextVariants = JSON.parse(data?.textContent || 'null'); }
      catch (_) { cache.delete(key); throw new Error('Missing product'); }
      if (!next || !Array.isArray(nextVariants) || !nextVariants.length) { cache.delete(key); throw new Error('Missing product'); }
      next.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
      if (version !== requestVersion || !dialog.open) return;
      cancelQuote();
      lastQuote = null;
      disposeProductUI();
      mount.replaceChildren(document.importNode(next, true));
      product = mount.querySelector('[data-rnf-qb-product]');
      product.hidden = !previousProduct;
      const imageReady = prepared ? Promise.resolve() : readyImage(product, controller.signal);
      disposeProductUI = mountProductUI(product);
      quoteRecoveries.clear();
      variants = nextVariants;
      currentVariant = variants.find(variant => String(variant.id) === product.querySelector('[name="id"]').value) || variants[0];
      mount.hidden = !previousProduct;
      dialog.removeAttribute('data-load-error');
      cache.delete(key);
      cache.set(key, { html, time: Date.now() });
      if (cache.size > 12) cache.delete(cache.keys().next().value);
      product.querySelector('[data-rnf-qb-gallery]')?.addEventListener('scroll', updateMediaCount, { passive: true });
      swapping = false;
      window.RNFStrapOffer?.decorate(product, offerContext, { select: choice => { if (busy) return; productUrl = choice.url; loadProduct(true); }, close: () => { if (!busy) close(); } });
      preparedPricing = prepared ? takePreparation(productUrl, prepared) : null;
      updateVariant(false);
      // The first visible product frame includes its confirmed price and offer.
      // Decode the photo while Shopify calculates only the selected purchase.
      let priceReady;
      do {
        priceReady = initialPriceReady;
        await Promise.all([imageReady, priceReady]);
      } while (priceReady !== initialPriceReady && version === requestVersion && !controller.signal.aborted);
      if (version !== requestVersion || !dialog.open) return;
      if (controller.signal.aborted) throw new Error('Product preparation timed out');
      state.hidden = true;
      product.hidden = false;
      mount.hidden = false;
      updateMediaCount();
      if (previousProduct) {
        product.querySelector('.rnf-qb__scroll').scrollTop = scrollTop;
        if (restoreChoiceFocus) product.querySelector('[data-rnf-strap-choice][aria-pressed="true"]')?.focus({ preventScroll: true });
        if (!reducedMotion()) product.animate?.([{ opacity: .65 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      } else if (offerContext && !window.matchMedia('(min-width: 990px)').matches) {
        dialog.style.height = `${Math.ceil(dialog.getBoundingClientRect().height)}px`;
        dialog.setAttribute('data-size-ready', '');
      }
      revealProduct();
    } catch (error) {
      if (version === requestVersion && dialog.open) {
        if (previousProduct) {
          const errorNode = previousProduct.querySelector('[data-rnf-qb-error]');
          errorNode.textContent = dialog.dataset.errorLabel;
          errorNode.hidden = false;
        } else { loadState(true); revealProduct(); }
      }
    } finally {
      clearTimeout(timeout);
      if (version === requestVersion) {
        swapping = false;
        dialog.removeAttribute('data-variant-loading');
        dialog.removeAttribute('aria-busy');
        setBusy(busy);
      }
    }
  }

  async function open(button, keyboard = false) {
    if (busy) return;
    if (closePromise) await closePromise;
    paymentAttempted = false;
    paymentController?.abort();
    secure.hidden = true;
    secureIntro.hidden = false;
    secureBack.hidden = false;
    secureStatus.hidden = true;
    secureStatus.removeAttribute('data-error');
    secureCart.hidden = true;
    secureCart.replaceChildren();
    secureViewCart.hidden = true;
    offerContext = button.rnfStrapOffer || null;
    product = null;
    currentVariant = null;
    dialog.classList.toggle('rnf-qb--strap', Boolean(offerContext));
    dialog.removeAttribute('data-size-ready');
    dialog.style.removeProperty('height');
    dialog.removeAttribute('aria-labelledby');
    opener = button.rnfReturnFocus || button;
    if (!offerContext || !button.rnfReturnFocus) keyboardOpener = keyboard;
    button.toggleAttribute('data-rnf-pointer', !keyboardOpener);
    dialog.toggleAttribute('data-rnf-pointer', !keyboardOpener);
    productUrl = button.dataset.rnfQuickBuy;
    viewport();
    if (!dialog.open) {
      lockPage();
      try { dialog.showModal(); } catch (error) { unlockPage(); location.assign(productUrl); return; }
    }
    if (keyboardOpener) (window.matchMedia('(min-width: 990px)').matches ? dialog.querySelector('.rnf-qb__close') || closeButton : closeButton).focus({ preventScroll: true });
    else dialog.focus({ preventScroll: true });
    mount.replaceChildren();
    loadState();
    // Warm Shopify's official wallet loader only after a purchase-panel intent.
    try { window.Shopify?.PaymentButton?.init?.(); } catch (_) {}
    loadProduct();
  }

  function updateMediaCount() {
    const gallery = product?.querySelector('[data-rnf-qb-gallery]');
    const count = product?.querySelector('[data-rnf-qb-media-count]');
    if (!gallery) return;
    const index = Math.min(gallery.children.length - 1, Math.max(0, Math.round(Math.abs(gallery.scrollLeft) / Math.max(1, gallery.clientWidth))));
    if (count) count.textContent = `${index + 1} / ${gallery.children.length}`;
    product.querySelectorAll('[data-rnf-qb-media-index]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.rnfQbMediaIndex) === index)));
    product.querySelectorAll('[data-rnf-qb-media-step]').forEach(button => { button.disabled = Number(button.dataset.rnfQbMediaStep) < 0 ? index === 0 : index === gallery.children.length - 1; });
    gallery.querySelectorAll('video').forEach(video => { if (video.closest('[data-media-id]') !== gallery.children[index]) video.pause(); });
  }

  function updateVariant(moveMedia = true) {
    const previousVariant = currentVariant;
    const previousQuantity = Number(product.querySelector('[name="quantity"]').value);
    const fields = [...product.querySelectorAll('[data-rnf-qb-option]')];
    const values = fields.map(field => field.querySelector('input:checked')?.value);
    if (fields.length) currentVariant = resolveVariant(variants, values);
    const input = product.querySelector('[name="id"]');
    input.value = currentVariant?.id || '';
    input.disabled = !currentVariant;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const details = new URL(product.dataset.productUrl, location.origin);
    if (currentVariant) details.searchParams.set('variant', currentVariant.id);
    product.querySelector('[data-rnf-qb-details]').href = details.pathname + details.search;
    const quantity = product.querySelector('[name="quantity"]');
    if (currentVariant) {
      quantity.min = currentVariant.min || 1;
      quantity.step = currentVariant.step || 1;
      if (currentVariant.max == null) quantity.removeAttribute('max');
      else quantity.max = currentVariant.max;
      quantity.value = normalizeQuantity(quantity.value, currentVariant);
      if (moveMedia && currentVariant.mediaId) changeVariantMedia(currentVariant.mediaId);
    }
    fields.forEach((field, index) => {
      field.querySelector('[data-rnf-qb-option-label]').textContent = field.querySelector('input:checked')?.dataset.label || '';
      field.querySelectorAll('input').forEach(radio => {
        const valuesForCandidate = [...values];
        valuesForCandidate[index] = radio.value;
        const candidate = resolveVariant(variants, valuesForCandidate);
        radio.closest('label').toggleAttribute('data-unavailable', !candidate?.available);
        const swatch = radio.closest('label').querySelector('[data-rnf-qb-swatch]');
        if (swatch && candidate?.swatch1) {
          swatch.style.setProperty('--rnf-sw1', candidate.swatch1);
          swatch.style.setProperty('--rnf-sw2', candidate.swatch2 || candidate.swatch1);
        }
      });
    });
    const samePricing = pricing && previousVariant && currentVariant && pricing.groupKey(previousVariant) === pricing.groupKey(currentVariant) && previousQuantity === Number(quantity.value);
    if (samePricing) {
      if (lastQuote) lastQuote = { ...lastQuote, variantId: currentVariant.id };
    } else if (!pricing) {
      lastQuote = null;
      startPricing();
    } else {
      applyQuantityPrice();
      if (currentVariant?.available) pricing.load(currentVariant, normalizeQuantity(quantity.value, currentVariant), true);
    }
    setBusy(busy);
    updateQuantityButtons();
  }

  async function changeVariantMedia(mediaId) {
    const selectedProduct = product;
    const gallery = product.querySelector('[data-rnf-qb-gallery]');
    const slide = gallery?.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    const version = ++mediaVersion;
    await readyImage(slide);
    if (version !== mediaVersion || selectedProduct !== product || !dialog.open) return;
    // Scroll only the image track; scrollIntoView also moves its enclosing sheet on iOS.
    const direction = getComputedStyle(gallery).direction === 'rtl' ? -1 : 1;
    gallery.scrollTo({ left: [...gallery.children].indexOf(slide) * gallery.clientWidth * direction, behavior: 'instant' });
    updateMediaCount();
    if (!reducedMotion()) gallery.animate?.([{ opacity: .65 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
  }

  function updateQuantityButtons() {
    if (!product) return;
    const quantity = product.querySelector('[name="quantity"]');
    const value = Number(quantity.value);
    product.querySelector('[data-rnf-qb-quantity-step="-1"]').disabled = busy || !currentVariant || value <= Number(quantity.min);
    product.querySelector('[data-rnf-qb-quantity-step="1"]').disabled = busy || !currentVariant || (quantity.max !== '' && value >= Number(quantity.max));
  }

  function money(cents) {
    return formatPrice(cents, product.dataset.currency, product.dataset.locale, product.dataset.country);
  }

  function updatePrice() {
    if (!product) return;
    const quantity = currentVariant ? normalizeQuantity(product.querySelector('[name="quantity"]').value, currentVariant) : 1;
    const validQuote = lastQuote?.available && lastQuote.variantId === currentVariant?.id && lastQuote.quantity === quantity;
    const unit = validQuote ? lastQuote.unitCents : currentVariant?.baseCents;
    const total = validQuote ? lastQuote.totalCents : unit * quantity;
    const unitText = Number.isFinite(unit) ? money(unit) : '—';
    const totalText = Number.isFinite(total) ? money(total) : '—';
    product.querySelector('[data-rnf-qb-unit-price]').textContent = unitText;
    product.dataset.totalPrice = totalText;
    product.querySelector('[data-rnf-qb-add-price]').textContent = `— ${totalText}`;
    const compare = product.querySelector('[data-rnf-qb-compare]');
    const reference = Math.max(currentVariant?.compareCents || 0, currentVariant?.baseCents || 0);
    compare.hidden = !Number.isFinite(unit) || reference <= unit;
    compare.textContent = compare.hidden ? '' : money(reference);
    const offer = product.querySelector('[data-rnf-qb-offer]');
    const priceNote = product.querySelector('[data-rnf-qb-price-note]');
    if (priceNote) priceNote.hidden = !(validQuote && lastQuote.estimated && lastQuote.reason === 'merged_line_rounding_estimate');
    if (validQuote) {
      offer.removeAttribute('data-pending');
      offer.removeAttribute('data-confirm');
      const saving = Math.max(0, currentVariant.baseCents * quantity - total);
      const titles = [...new Set((lastQuote.discounts || []).map(discount => discount.title).filter(Boolean))];
      offer.hidden = saving <= 0;
      offer.replaceChildren();
      if (saving > 0) {
        const tag = document.createElement('span');
        tag.className = 'rnf-qb__offer-tag';
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '🏷️';
        const title = document.createElement('span');
        title.textContent = titles.join(' + ') || product.dataset.discountsLabel;
        tag.append(icon, title);
        const savings = document.createElement('span');
        savings.className = 'rnf-qb__offer-saving';
        savings.textContent = product.dataset.savingsLabel.replace('__AMOUNT__', money(saving));
        offer.append(tag, savings);
      }
    }
    window.RNFStrapOffer?.updatePrice(product, offerContext, { unit, base: currentVariant?.baseCents, compare: currentVariant?.compareCents, quote: validQuote ? lastQuote : null });
    if (!secure.hidden && !paymentAttempted) syncPaymentSelection();
  }

  function cancelQuote() {
    clearTimeout(quoteRecoveryTimer);
    quoteRecoveryTimer = null;
    quoteVersion++;
    quoteController?.abort();
    quoteSession?.dispose();
    quoteSession = null;
    pricing = null;
  }

  function recoverSelectedQuote(result, quantity) {
    clearTimeout(quoteRecoveryTimer);
    quoteRecoveryTimer = null;
    if (!result || result.available || result.retryable !== true || !pricing || !currentVariant?.available || busy || !dialog.open) return;
    const selectedProduct = product;
    const group = pricing.groupKey(currentVariant);
    const key = `${group}:${quantity}`;
    if (quoteRecoveries.has(key)) return;
    const version = quoteVersion;
    quoteRecoveryTimer = setTimeout(() => {
      quoteRecoveryTimer = null;
      if (version !== quoteVersion || product !== selectedProduct || !dialog.open || busy || !currentVariant?.available || pricing?.groupKey(currentVariant) !== group) return;
      const selectedQuantity = normalizeQuantity(product.querySelector('[name="quantity"]').value, currentVariant);
      const latest = pricing.read(currentVariant, selectedQuantity);
      if (selectedQuantity !== quantity || !latest || latest.available || latest.retryable !== true) return;
      quoteRecoveries.add(key);
      if (quoteSession?.available === false && quoteSession.retryable === true) startPricing();
      else pricing.load(currentVariant, quantity, true);
    }, 800);
  }

  function applyQuantityPrice() {
    if (!product || !currentVariant) { lastQuote = null; updatePrice(); return; }
    const quantity = normalizeQuantity(product.querySelector('[name="quantity"]').value, currentVariant);
    const result = pricing?.read(currentVariant, quantity);
    if (result?.available && result.currency === product.dataset.currency && Number.isFinite(result.unitCents) && Number.isFinite(result.totalCents)) {
      lastQuote = { ...result, variantId: currentVariant.id, quantity };
    } else if (lastQuote?.available && lastQuote.group === pricing?.groupKey(currentVariant)) {
      lastQuote = { ...lastQuote, variantId: currentVariant.id, quantity, totalCents: Math.round(lastQuote.unitCents * quantity), estimated: true, reason: 'quantity_estimate' };
    } else {
      lastQuote = null;
    }
    updatePrice();
    recoverSelectedQuote(result, quantity);
    const offer = product.querySelector('[data-rnf-qb-offer]');
    if ((result && !result.available) || (lastQuote?.estimated && lastQuote.reason !== 'merged_line_rounding_estimate') || (!lastQuote && !offer.hasAttribute('data-pending'))) {
      offer.removeAttribute('data-pending');
      offer.setAttribute('data-confirm', '');
      offer.textContent = product.dataset.confirmLabel;
      offer.hidden = false;
    }
  }

  function startPricing() {
    cancelQuote();
    if (!product || busy) return;
    const selectedProduct = product;
    quoteController = new AbortController();
    const controller = quoteController;
    const version = quoteVersion;
    let settleInitial;
    initialPriceReady = new Promise(resolve => { settleInitial = resolve; });
    controller.signal.addEventListener('abort', settleInitial, { once: true });
    const initialVariant = currentVariant;
    const initialQuantity = initialVariant ? normalizeQuantity(selectedProduct.querySelector('[name="quantity"]').value, initialVariant) : 1;
    let nextQuantityWarmed = false;
    const offer = selectedProduct.querySelector('[data-rnf-qb-offer]');
    if (!lastQuote?.available) {
      offer.hidden = false;
      offer.setAttribute('data-pending', '');
      offer.removeAttribute('data-confirm');
      offer.textContent = `${selectedProduct.dataset.discountsLabel}…`;
    }
    const preparedSession = preparedPricing;
    preparedPricing = null;
    const sessionReady = (preparedSession ? Promise.resolve(preparedSession) : window.RNFQuickBuyQuote.createSession({ country: selectedProduct.dataset.country, currency: selectedProduct.dataset.currency, loggedIn: selectedProduct.dataset.customer === 'true', signal: controller.signal, lineProperties: cartLineProperties() })).then(session => {
      if (controller.signal.aborted) { session.dispose(); return session; }
      quoteSession = session;
      return session;
    });
    const orderedVariants = [currentVariant, ...variants].filter(Boolean);
    pricing = createQuantityPricing(async request => (await sessionReady).getQuote(request), orderedVariants, (quotedVariant, quotedQuantity) => {
      if (version !== quoteVersion || !dialog.open || selectedProduct !== product || busy) return;
      const quantity = currentVariant ? normalizeQuantity(product.querySelector('[name="quantity"]').value, currentVariant) : 1;
      if (currentVariant && quotedQuantity === quantity && pricing.groupKey(quotedVariant) === pricing.groupKey(currentVariant) && pricing.read(currentVariant, quantity)) applyQuantityPrice();
      if (initialVariant && quotedQuantity === initialQuantity && pricing.groupKey(quotedVariant) === pricing.groupKey(initialVariant)) {
        settleInitial();
        // One adjacent quantity can be reused instantly; never create ten speculative carts.
        const initialResult = pricing.read(initialVariant, initialQuantity);
        if (!nextQuantityWarmed && initialResult?.available) {
          nextQuantityWarmed = true;
          const nextQuantity = normalizeQuantity(initialQuantity + (initialVariant.step || 1), initialVariant);
          if (nextQuantity !== initialQuantity) pricing.load(initialVariant, nextQuantity);
        }
      }
    }, controller.signal);
    if (currentVariant?.available) {
      const quantity = normalizeQuantity(product.querySelector('[name="quantity"]').value, currentVariant);
      pricing.prime(currentVariant, quantity);
    } else settleInitial();
    applyQuantityPrice();
  }

  function requestQuote() {
    applyQuantityPrice();
    if (!currentVariant?.available || !product || busy) return;
    if (!pricing || (quoteSession?.available === false && quoteSession.retryable === true)) { startPricing(); return; }
    const quantity = normalizeQuantity(product.querySelector('[name="quantity"]').value, currentVariant);
    pricing.load(currentVariant, quantity, true);
  }

  function setBusy(value) {
    busy = value;
    if (!value && !dialog.open) drainWarmQueue();
    dialog.querySelectorAll('[data-rnf-qb-close]').forEach(button => { button.disabled = value; });
    secureBack.disabled = value;
    secureViewCart.disabled = value;
    if (!product) return;
    product.querySelectorAll('[data-rnf-qb-option] input,[name="quantity"],[data-rnf-strap-choice],.rnf-strap__dismiss').forEach(input => { input.disabled = value; });
    const add = product.querySelector('.rnf-qb__add');
    const requiresPlan = product.dataset.requiresPlan === 'true';
    add.disabled = value || swapping || !currentVariant?.available || requiresPlan;
    add.setAttribute('aria-busy', String(value));
    product.querySelector('[data-rnf-qb-secure-open]').disabled = value || swapping || !currentVariant?.available || requiresPlan;
    product.querySelector('[data-rnf-qb-add-label]').textContent = value ? product.dataset.addingLabel : !currentVariant || requiresPlan ? product.dataset.unavailableLabel : currentVariant.available ? product.dataset.addLabel : product.dataset.soldOutLabel;
    product.querySelector('[data-rnf-qb-add-price]').hidden = value || !currentVariant?.available || requiresPlan;
    updateQuantityButtons();
  }

  function cartLineProperties() {
    return offerContext ? { _rnf_parent_watch_variant: String(offerContext.key), _rnf_parent_watch: String(offerContext.title) } : {};
  }

  function cartLine(id, quantity) {
    return offerContext ? { id, quantity, properties: cartLineProperties() } : { id, quantity };
  }

  function addCycle() {
    if (reducedMotion()) return Promise.resolve();
    const animations = [...product.querySelectorAll('.rnf-qb__busy-dots i')]
      .flatMap(dot => dot.getAnimations?.() || [])
      .filter(animation => Number.isFinite(animation.effect?.getComputedTiming().iterations));
    return new Promise(resolve => {
      // One wave only; a slow server response leaves the three dots still.
      const timer = setTimeout(resolve, 800);
      if (animations.length) Promise.allSettled(animations.map(animation => animation.finished)).then(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  const feedbackTimers = new WeakMap();
  function confirmAdded(button) {
    const card = button?.matches('.rnf-qb__trigger') ? button : null;
    const bubble = document.getElementById('cart-icon-bubble');
    [card, bubble].filter(Boolean).forEach(element => {
      clearTimeout(feedbackTimers.get(element));
      element.removeAttribute('data-rnf-add-feedback');
      if (element === card) element.setAttribute('data-rnf-added', '');
      // Commit the reset so another confirmed add can restart the same animation.
      void element.offsetWidth;
      element.setAttribute('data-rnf-add-feedback', '');
      feedbackTimers.set(element, setTimeout(() => {
        element.removeAttribute('data-rnf-add-feedback');
        element.removeAttribute('data-rnf-added');
        feedbackTimers.delete(element);
      }, 1400));
    });
    return new Promise(resolve => setTimeout(resolve, reducedMotion() ? 0 : card ? 920 : 680));
  }

  async function submit(event) {
    if (event.target !== product?.querySelector('form')) return;
    event.preventDefault();
    if (busy || swapping || !currentVariant?.available || product.dataset.requiresPlan === 'true') return;
    const quantityInput = product.querySelector('[name="quantity"]');
    if (!quantityInput.reportValidity()) return;
    const id = currentVariant.id, quantity = Number(quantityInput.value), originalOpener = opener, addingStrap = Boolean(offerContext);
    const errorNode = product.querySelector('[data-rnf-qb-error]');
    errorNode.hidden = true;
    cancelQuote();
    setBusy(true);
    const cycle = addCycle();
    try {
      // The quick-buy asset can finish downloading before the deferred cart engine.
      // Give that engine its initialization turn before choosing the no-JS fallback.
      if (!window.RNFCart?.run && document.querySelector('script[src*="/rnf-native-cart.js"]') && window.customElements) {
        let readyTimer;
        await Promise.race([window.customElements.whenDefined('cart-drawer'), new Promise(resolve => { readyTimer = setTimeout(resolve, 2000); })]);
        clearTimeout(readyTimer);
      }
      if (window.RNFCart?.run) {
        await Promise.all([window.RNFCart.run(api => api.add({ items: [cartLine(id, quantity)] }), { open: false, opener: originalOpener }), cycle]);
        if (!addingStrap) window.RNFStrapOffer?.prepare?.(id);
        await close(false, true);
        if (!addingStrap && originalOpener?.matches('.rnf-qb__trigger')) {
          await confirmAdded(originalOpener);
          setBusy(false);
          if (window.RNFCart.isReady?.() !== false && window.RNFStrapOffer?.afterAdd?.(id, originalOpener)) return;
        }
        const drawer = document.getElementById('RnfCartDrawer');
        if (drawer?.reveal) drawer.reveal(originalOpener);
        else location.assign(product.dataset.cartUrl);
      } else {
        const response = await fetch(product.querySelector('form').action + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [cartLine(id, quantity)] }) });
        const data = await response.json();
        if (!response.ok) {
          const error = new Error(product.dataset.errorLabel);
          error.payload = data;
          throw error;
        }
        location.assign(product.dataset.cartUrl);
      }
    } catch (error) {
      errorNode.textContent = error.payload?.description || error.payload?.message || product.dataset.errorLabel;
      errorNode.hidden = false;
    } finally { setBusy(false); }
  }

  function syncPaymentSelection() {
    secure.querySelector('[data-rnf-qb-secure-product]').textContent = product.querySelector('.rnf-qb__title').textContent;
    secure.querySelector('[data-rnf-qb-secure-variant]').textContent = [...product.querySelectorAll('.rnf-qb__option legend')].map(legend => legend.textContent.trim()).join(' · ');
    const quantity = product.querySelector('[name="quantity"]');
    secure.querySelector('[data-rnf-qb-secure-quantity]').textContent = `${quantity.getAttribute('aria-label')}: ${quantity.value}`;
    const price = product.dataset.totalPrice;
    secure.querySelector('[data-rnf-qb-secure-price]').textContent = price;
  }

  async function openPayment() {
    if (busy || paymentAttempted || !currentVariant?.available || product.dataset.requiresPlan === 'true') return;
    const quantityInput = product.querySelector('[name="quantity"]');
    if (!quantityInput.reportValidity()) return;
    const selectedProduct = product;
    const errorNode = selectedProduct.querySelector('[data-rnf-qb-error]');
    errorNode.hidden = true;
    const checkout = new URL(selectedProduct.dataset.cartUrl, location.origin);
    if (checkout.origin !== location.origin || !/\/cart\/?$/.test(checkout.pathname)) return;
    checkout.pathname = checkout.pathname.replace(/\/cart\/?$/, '/checkout');
    checkout.search = '';
    checkout.hash = '';
    const line = cartLine(currentVariant.id, Number(quantityInput.value));
    cancelQuote();
    setBusy(true);
    try {
      if (!window.RNFCart?.run && document.querySelector('script[src*="/rnf-native-cart.js"]') && window.customElements) {
        let timer;
        await Promise.race([window.customElements.whenDefined('cart-drawer'), new Promise(resolve => { timer = setTimeout(resolve, 2000); })]);
        clearTimeout(timer);
      }
      if (!window.RNFCart?.run) throw new Error('Cart unavailable');
      await window.RNFCart.run(api => api.add({ items: [line] }), { open: false, strict: true, opener });
      paymentAttempted = true;
      location.assign(checkout.href);
    } catch (error) {
      paymentAttempted = false;
      errorNode.textContent = error.payload?.description || error.payload?.message || selectedProduct.dataset.errorLabel;
      errorNode.hidden = false;
      setBusy(false);
      startPricing();
    }
  }


  async function viewPaymentCart() {
    if (busy) return;
    await close(false);
    const drawer = document.getElementById('RnfCartDrawer');
    if (drawer?.open) await drawer.open(opener);
    else location.assign(product.dataset.cartUrl);
  }

  async function preparePayment() {
    if (busy || paymentAttempted || !currentVariant?.available) return;
    const quantityInput = product.querySelector('[name="quantity"]');
    if (!quantityInput.reportValidity()) return;
    const id = currentVariant.id, quantity = Number(quantityInput.value);
    paymentAttempted = true;
    cancelQuote();
    setBusy(true);
    secureIntro.hidden = true;
    secureBack.hidden = true;
    secureStatus.hidden = false;
    secureStatus.textContent = secure.dataset.preparingLabel;
    try {
      if (!window.RNFCart?.run) throw new Error('Cart unavailable');
      await window.RNFCart.run(api => api.add({ items: [cartLine(id, quantity)] }), { open: false, strict: true, opener });
      paymentController = new AbortController();
      const controller = paymentController;
      const url = new URL(product.dataset.cartUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid cart URL');
      url.searchParams.set('section_id', 'rnf-quick-buy');
      const timeout = setTimeout(() => controller.abort(), 15000);
      let html;
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Payment section unavailable');
        html = await response.text();
      } finally { clearTimeout(timeout); }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-rnf-qb-payment-cart]');
      if (!next || !next.querySelector('button[name="checkout"]:not([disabled])')) throw new Error('Payment cart unavailable');
      next.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
      secureCart.replaceChildren(document.importNode(next, true));
      secureCart.hidden = false;
      secureStatus.hidden = true;
      try { window.Shopify?.PaymentButton?.init?.(); } catch (_) {}
    } catch (error) {
      secureStatus.textContent = secure.dataset.errorLabel;
      secureStatus.setAttribute('data-error', '');
    } finally {
      secureViewCart.hidden = false;
      setBusy(false);
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-rnf-quick-buy]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    open(button, event.detail === 0);
  }, true);
  let warmTimer;
  const warmIntent = event => {
    const button = event.target.closest('[data-rnf-quick-buy]') || event.target.closest('.card-wrapper,.rnf-nav-card')?.querySelector('[data-rnf-quick-buy]');
    if (!button || dialog.open || busy) return;
    clearTimeout(warmTimer);
    warmTimer = setTimeout(() => queuePreparation(button), event.type === 'pointerover' ? 80 : 0);
  };
  document.addEventListener('pointerover', warmIntent, { passive: true });
  document.addEventListener('focusin', warmIntent);
  document.addEventListener('touchstart', warmIntent, { passive: true });
  document.addEventListener('pointerout', event => { if (event.target.closest('[data-rnf-quick-buy]')) clearTimeout(warmTimer); }, { passive: true });
  window.RNFQuickBuy = { prefetch: value => productHtml(value, true).catch(() => null), prepare: value => prepareProduct(value).then(() => true).catch(() => false) };
  dialog.addEventListener('cancel', event => { event.preventDefault(); if (!busy) close(); });
  dialog.addEventListener('click', event => {
    if (event.target.closest('[data-rnf-qb-close]')) { if (!busy && performance.now() >= suppressCloseClickUntil) close(); return; }
    if (event.target === dialog && !busy) { close(); return; }
    if (event.target.closest('[data-rnf-qb-retry]')) { loadProduct(); return; }
    if (event.target.closest('[data-rnf-qb-secure-open]')) { openPayment(); return; }
    if (event.target.closest('[data-rnf-qb-secure-view-cart]')) { viewPaymentCart(); return; }
    if (event.target.closest('[data-rnf-qb-secure-back]') && !busy && !paymentAttempted) {
      secure.hidden = true;
      mount.hidden = false;
      product.querySelector('[data-rnf-qb-secure-open]').focus({ preventScroll: true });
      return;
    }
    const quantityButton = event.target.closest('[data-rnf-qb-quantity-step]');
    if (quantityButton && !busy && currentVariant) {
      const input = product.querySelector('[name="quantity"]');
      input.value = normalizeQuantity(Number(input.value) + Number(quantityButton.dataset.rnfQbQuantityStep) * (currentVariant.step || 1), currentVariant);
      updateQuantityButtons();
      requestQuote();
    }
    const mediaDot = event.target.closest('[data-rnf-qb-media-index]');
    const mediaStep = event.target.closest('[data-rnf-qb-media-step]');
    if ((mediaDot || mediaStep) && product) {
      const gallery = product.querySelector('[data-rnf-qb-gallery]');
      const direction = getComputedStyle(gallery).direction === 'rtl' ? -1 : 1;
      const current = Math.round(Math.abs(gallery.scrollLeft) / Math.max(1, gallery.clientWidth));
      const index = mediaDot ? Number(mediaDot.dataset.rnfQbMediaIndex) : Math.min(gallery.children.length - 1, Math.max(0, current + Number(mediaStep.dataset.rnfQbMediaStep)));
      gallery.scrollTo({ left: index * gallery.clientWidth * direction, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  });
  dialog.addEventListener('change', event => {
    if (event.target.closest('[data-rnf-qb-option]')) updateVariant();
    if (event.target.name === 'quantity' && currentVariant) {
      event.target.value = normalizeQuantity(event.target.value, currentVariant);
      updateQuantityButtons();
      requestQuote();
    }
  });
  dialog.addEventListener('input', event => {
    if (event.target.name === 'quantity' && currentVariant && !busy) requestQuote();
  });
  document.addEventListener('pointerdown', () => {
    if (!dialog.open) return;
    keyboardOpener = false;
    dialog.setAttribute('data-rnf-pointer', '');
  }, { capture: true, passive: true });
  document.addEventListener('keydown', event => {
    if (event.key === 'Tab') {
      if (dialog.open) keyboardOpener = true;
      document.querySelectorAll('[data-rnf-pointer]').forEach(element => element.removeAttribute('data-rnf-pointer'));
    }
  });
  document.addEventListener('rnf:cart:updated', () => {
    invalidatePreparedProducts();
    if (!dialog.open) warmVisibleProducts();
    if (dialog.open && product && !busy && !paymentAttempted) {
      quoteRecoveries.clear();
      startPricing();
    }
  });

  function resetDrag() {
    drag = null;
    dialog.removeAttribute('data-dragging');
    dialog.style.removeProperty('transform');
  }

  dragHeader.addEventListener('pointerdown', event => {
    if (busy || closePromise || event.button !== 0) return;
    drag = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), distance: 0 };
    dragHeader.setPointerCapture(event.pointerId);
  });
  dragHeader.addEventListener('pointermove', event => {
    if (!drag || event.pointerId !== drag.id) return;
    const distance = Math.max(0, event.clientY - drag.y);
    if (distance < 6 || Math.abs(event.clientX - drag.x) > distance) return;
    drag.distance = distance;
    dialog.setAttribute('data-dragging', '');
    dialog.style.transform = `translateY(${distance}px)`;
  });
  const releaseDrag = event => {
    if (!drag || event.pointerId !== drag.id) return;
    const saved = drag;
    if (saved.distance > 6) suppressCloseClickUntil = performance.now() + 350;
    if (dragHeader.hasPointerCapture(saved.id)) dragHeader.releasePointerCapture(saved.id);
    resetDrag();
    if (event.type !== 'pointercancel' && shouldDismissDrag(saved.distance, performance.now() - saved.time)) close();
  };
  dragHeader.addEventListener('pointerup', releaseDrag);
  dragHeader.addEventListener('pointercancel', releaseDrag);
  dialog.addEventListener('submit', submit);
  window.addEventListener('resize', viewport, { passive: true });
  window.visualViewport?.addEventListener('resize', viewport, { passive: true });
  window.visualViewport?.addEventListener('scroll', viewport, { passive: true });
  document.documentElement.classList.add('rnf-qb-ready');
})();








